import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { responsiveImg } from "@/lib/api";

// Cinematic hero with dark overlay, noise + radar motif. `tall` for homepage.
export function Hero({ eyebrow, title, subtitle, image, children, tall = false }) {
  const reduce = useReducedMotion();
  const img = image ? responsiveImg(image, { maxWidth: 1920 }) : null;
  return (
    <section
      className={`relative isolate flex items-end overflow-hidden ${tall ? "min-h-[88vh]" : "min-h-[62vh]"}`}
      data-testid="hero"
    >
      {img ? (
        <img
          src={img.src}
          srcSet={img.srcSet}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-ink-800" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/55" />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute inset-0 tech-grid opacity-30" />

      {/* radar motif */}
      <div className="pointer-events-none absolute -right-24 -top-24 w-[420px] h-[420px] opacity-[0.18] hidden md:block">
        <div className={`w-full h-full rounded-full border border-cyan/40 ${reduce ? "" : "animate-radar"}`}
          style={{ background: "conic-gradient(from 0deg, rgba(0,212,255,0.25), transparent 25%)" }} />
        <div className="absolute inset-8 rounded-full border border-cyan/20" />
        <div className="absolute inset-20 rounded-full border border-cyan/10" />
      </div>

      <div className="container-x relative z-10 pb-16 pt-28 md:pb-24">
        <div className="max-w-3xl">
          {eyebrow ? (
            <motion.div
              className="eyebrow mb-4"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {eyebrow}
            </motion.div>
          ) : null}
          <motion.h1
            className="h-display text-cloud text-[clamp(2.75rem,6vw+0.5rem,5.5rem)]"
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            {title}
          </motion.h1>
          {subtitle ? (
            <motion.p
              className="mt-6 text-lg md:text-xl text-steel max-w-2xl leading-relaxed"
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              {subtitle}
            </motion.p>
          ) : null}
          {children ? (
            <motion.div
              className="mt-9 flex flex-wrap gap-4"
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              {children}
            </motion.div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
